import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, cn } from '@zafroi/ui-kit';
import { Bot, Check, Send } from 'lucide-react';
import { getBotConversation, getBotIntro, previewBot } from '@/services/engage-api';
import { relativeTime } from '@/lib/format';
import type { BotRaisedRequest } from '@/types/engage';

/**
 * CustomerChatWidget — a realistic preview of what an END CUSTOMER sees
 * when chatting with the tenant's bot.
 *
 * Deliberately free of admin diagnostic chrome: no "grounded" badge, no
 * sources list, never the word "escalated". When the bot pipeline returns
 * `handoff: true` we show the bot reply normally, then a calm, muted system
 * line — a status, not a warning. This is the view a tenant demos to their
 * beta customer.
 */

interface CustomerMessage {
  id: string;
  role: 'customer' | 'bot';
  content: string;
  /** Bot messages only — when the conversation has been handed to a human. */
  handoff?: boolean;
  /** Bot messages only — set when the bot raised a support request. */
  requestRaised?: BotRaisedRequest | null;
  /**
   * Bot messages only — contextual quick-reply chips. Only rendered for the
   * latest bot message (see `CustomerChatWidget`).
   */
  suggestions?: string[];
}

export interface CustomerChatWidgetProps {
  botName: string;
  greeting: string;
  /**
   * When set, the bot is grounded on this customer's 360 — it can answer
   * their account questions and raise support requests for them.
   */
  customerId?: string;
}

export function CustomerChatWidget({
  botName,
  greeting,
  customerId,
}: CustomerChatWidgetProps) {
  const trimmedName = botName.trim() || 'Assistant';
  const trimmedGreeting =
    greeting.trim() || `Hi! I'm ${trimmedName} — how can we help today?`;

  const openingMessage = useMemo<CustomerMessage>(
    () => ({
      id: 'cm_greeting',
      role: 'bot',
      content: trimmedGreeting,
    }),
    [trimmedGreeting],
  );

  const [messages, setMessages] = useState<CustomerMessage[]>([openingMessage]);
  const [draft, setDraft] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // The opening menu — KB topic chips + standing actions, fetched on mount.
  const [introSuggestions, setIntroSuggestions] = useState<string[]>([]);
  // Restored bot-chat history for the previewing customer. When non-empty,
  // we seed the thread with these turns, render a "continued from" divider
  // before any new messages, and suppress the fresh-open greeting/intro.
  const [historyCount, setHistoryCount] = useState(0);
  const [historyLastAt, setHistoryLastAt] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  // Keep the opening greeting live as the tenant edits the Identity card,
  // but only while the customer has not started chatting yet AND there is
  // no restored history — once history is loaded, the greeting is dropped.
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 1 && prev[0].id === 'cm_greeting'
        ? [openingMessage]
        : prev,
    );
  }, [openingMessage]);

  // Fetch the opening quick-reply menu once, when the widget mounts.
  useEffect(() => {
    let active = true;
    getBotIntro()
      .then((intro) => {
        if (active) setIntroSuggestions(intro.suggestions);
      })
      .catch(() => {
        if (active) setIntroSuggestions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Restore the customer's previous bot-chat history. Runs on mount and
  // whenever `customerId` changes (the "Previewing as" selector switches
  // customer) — we always reset to a clean state first to avoid stacking
  // histories. Anonymous mode (no customerId) keeps the fresh greeting.
  useEffect(() => {
    let active = true;

    if (!customerId) {
      // Anonymous: reset to the fresh greeting + intro behaviour.
      setMessages([openingMessage]);
      setHistoryCount(0);
      setHistoryLastAt(null);
      return () => {
        active = false;
      };
    }

    // Reset immediately so we never stack the previous customer's history
    // while the new fetch is in flight.
    setMessages([openingMessage]);
    setHistoryCount(0);
    setHistoryLastAt(null);

    getBotConversation(customerId)
      .then((history) => {
        if (!active) return;
        if (history.messages.length === 0) {
          // Empty history → keep the fresh-open behaviour unchanged.
          return;
        }
        const restored: CustomerMessage[] = history.messages.map((m) => ({
          id: `cm_hist_${m.id}`,
          role: m.role === 'user' ? 'customer' : 'bot',
          content: m.content,
        }));
        // History present → drop the fresh greeting; the divider takes its
        // place and the chat resumes from where it left off.
        setMessages(restored);
        setHistoryCount(restored.length);
        setHistoryLastAt(history.lastMessageAt);
      })
      .catch(() => {
        // Swallow — fall back to fresh-chat behaviour already set above.
      });

    return () => {
      active = false;
    };
    // `openingMessage` is intentionally excluded: it is re-applied via the
    // greeting-sync effect above; including it here would re-fetch history
    // on every keystroke in the Identity card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isTyping]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;

    const history = messages.map((m) => ({
      role: m.role === 'customer' ? ('user' as const) : ('assistant' as const),
      content: m.content,
    }));

    setMessages((prev) => [
      ...prev,
      { id: `cm_${Date.now()}`, role: 'customer', content: trimmed },
    ]);
    setDraft('');
    setIsTyping(true);

    try {
      const reply = await previewBot(trimmed, history, customerId);
      setMessages((prev) => [
        ...prev,
        {
          id: `cm_${Date.now()}_bot`,
          role: 'bot',
          content: reply.reply,
          handoff: reply.handoff,
          requestRaised: reply.requestRaised,
          suggestions: reply.suggestions,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `cm_${Date.now()}_err`,
          role: 'bot',
          content:
            'Sorry, something went wrong on our end. Please try sending that again.',
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="flex h-[560px] w-[380px] max-w-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-elev-3 animate-fade-in">
      {/* Header — brand tone */}
      <div className="flex items-center gap-3 bg-primary px-4 py-3.5 text-primary-foreground">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 text-base font-semibold">
          <Bot className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-tight">
            {trimmedName}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-primary-foreground/80">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-300" />
            Typically replies instantly
          </div>
        </div>
      </div>

      {/* Message thread */}
      <div
        ref={threadRef}
        className="flex-1 space-y-3 overflow-y-auto bg-muted/30 px-4 py-4"
      >
        {messages.map((message, index) => {
          // Only the latest bot message offers chips. Before the customer
          // says anything, the greeting carries the fetched opening menu;
          // after that, the most recent bot reply carries its own. When
          // history was restored, the greeting is gone — so the historical
          // bot messages never carry the intro menu (which would look odd
          // attached to yesterday's reply).
          const isLatestBot =
            message.role === 'bot' &&
            !messages.slice(index + 1).some((m) => m.role === 'bot');
          const isHistoryMessage = index < historyCount;
          const chips =
            isLatestBot && !isTyping && !isHistoryMessage
              ? message.id === 'cm_greeting'
                ? introSuggestions
                : (message.suggestions ?? [])
              : [];
          // Divider sits AFTER the last restored message and BEFORE any
          // current-session messages. Only renders when history loaded.
          const showDividerAfter =
            historyCount > 0 && index === historyCount - 1;
          return (
            <div key={message.id} className="contents">
              <MessageBubble
                message={message}
                suggestions={chips}
                onPickSuggestion={(label) => void send(label)}
                suggestionsDisabled={isTyping}
              />
              {showDividerAfter && (
                <HistoryDivider lastAt={historyLastAt} />
              )}
            </div>
          );
        })}
        {isTyping && <TypingIndicator />}
      </div>

      {/* Composer */}
      <form
        className="border-t border-border bg-card p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
      >
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(draft);
              }
            }}
            rows={1}
            placeholder="Type a message…"
            className="max-h-28 min-h-9 flex-1 resize-none rounded-full border border-input bg-background px-4 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button
            type="submit"
            size="icon"
            className="shrink-0 rounded-full"
            disabled={!draft.trim() || isTyping}
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({
  message,
  suggestions,
  onPickSuggestion,
  suggestionsDisabled,
}: {
  message: CustomerMessage;
  /** Quick-reply chips to render beneath this bot message — latest bot only. */
  suggestions: string[];
  onPickSuggestion: (label: string) => void;
  suggestionsDisabled: boolean;
}) {
  if (message.role === 'customer') {
    return (
      <div className="flex justify-end animate-slide-up">
        <div className="max-w-[80%] whitespace-pre-line rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm text-primary-foreground shadow-elev-1">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 animate-slide-up">
      <div className="flex items-end gap-2">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bot className="h-3.5 w-3.5" />
        </span>
        <div className="max-w-[80%] whitespace-pre-line rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-2 text-sm text-foreground shadow-elev-1">
          {message.content}
        </div>
      </div>
      {message.requestRaised && (
        <p className="ml-9 inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
          <Check className="h-3 w-3 shrink-0" />
          Support request {message.requestRaised.reference} created
        </p>
      )}
      {suggestions.length > 0 && (
        <QuickReplies
          suggestions={suggestions}
          onPick={onPickSuggestion}
          disabled={suggestionsDisabled}
        />
      )}
    </div>
  );
}

/** Tappable quick-reply pills shown beneath the latest bot message. */
function QuickReplies({
  suggestions,
  onPick,
  disabled,
}: {
  suggestions: string[];
  onPick: (label: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="ml-9 flex flex-wrap gap-1.5 pt-0.5">
      {suggestions.map((label) => (
        <button
          key={label}
          type="button"
          disabled={disabled}
          onClick={() => onPick(label)}
          className={cn(
            'rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-xs font-medium text-primary',
            'transition-colors hover:border-primary hover:bg-primary/10',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Centred horizontal-rule separator marking the boundary between restored
 * history and the current session. Borrows the widget's muted palette so it
 * reads as ambient context, not as a system warning.
 */
function HistoryDivider({ lastAt }: { lastAt: string | null }) {
  // `relativeTime` returns the "ago" form already (e.g. "1 day ago",
  // "3 hours ago") — close enough to natural language ("yesterday") for
  // this small subtitle. No need for a bespoke formatter.
  const when = lastAt ? relativeTime(lastAt) : null;
  return (
    <div
      className="flex items-center gap-2 px-1 py-1 animate-fade-in"
      role="separator"
      aria-label="Continued from your previous conversation"
    >
      <span className="h-px flex-1 bg-border" />
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Continued from your previous conversation
        {when && <span className="ml-1 normal-case font-normal">· {when}</span>}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 animate-fade-in">
      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bot className="h-3.5 w-3.5" />
      </span>
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3 shadow-elev-1">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60',
            )}
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}
